'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useEffect, useState } from 'react';

import { usePathname } from 'next/navigation';
import { pathToRegexp } from 'path-to-regexp';
import PropTypes from 'prop-types';

import useGeolocation from 'hooks/useGeolocation';
import { localStorageSupport, saveToLocalStorage } from 'utils';

import Card from './Card';
import Overlay from './Overlay';
import SignUp from './SignUp';

export const ALERT_STATE = {
    CLOSED: 1,
    MINIMIZED: 2,
};

const alertMap = {
    AlertOverlay: Overlay,
    AlertCard: Card,
    AlertSignUp: SignUp,
};

const Alerts = ({ alerts }) => {
    const pathname = usePathname();

    const [alert, updateAlert] = useState(null);
    const { geoLocation } = useGeolocation();
    const [hasOneTrustInteracted, setOneTrustInteracted] = useState(false);

    const uid = `tcca_alert_${alert?.sys?.id}_${new Date(
        alert?.sys?.publishedAt
    ).getTime()}`; // add publishedAt to reset when changes

    const disallowedQueryParams = [
        'utm_campaign',
        'utm_medium',
        'utm_source',
        '_kx',
        'fbid',
    ];

    const url = new URL(pathname, 'https://www.tillamook.com');

    disallowedQueryParams.forEach(param => {
        url.searchParams.delete(param);
    });

    // Convert route strings that use a bare wildcard (.*) into a path-to-regexp
    // compatible named parameter pattern. e.g., "/foodservice(.*)" -> "/foodservice/:any(.*)"
    // This prevents "Missing parameter name" errors in path-to-regexp v6.
    const normalizeRoutes = routes => {
        const arr = Array.isArray(routes) ? routes : [routes];
        const out = [];
        for (const r of arr) {
            if (typeof r !== 'string') {
                out.push(r);
                continue;
            }
            if (r.includes('(.*)')) {
                // Add base path without the wildcard and a catch-all variant
                out.push(r.replace(/\(\.\*\)/g, ''));
                out.push(r.replace(/\(\.\*\)/g, '/*any'));
            } else {
                out.push(r);
            }
        }
        return out;
    };

    const matchIncluded = (isGlobal, includedRoutes) => {
        // If global, we are clear to proceed
        if (isGlobal) {
            return true;
        }

        // No included routes = no render
        if (!includedRoutes?.length) {
            return false;
        }

        // Wrap in try/catch to avoid any errors with misformatted routes
        // If in doubt, do not render
        try {
            const normalized = normalizeRoutes(includedRoutes);
            // Support arrays of routes; match if ANY matches
            return normalized.some(route => {
                try {
                    const regexp = pathToRegexp(route)?.regexp;
                    return regexp?.test(pathname);
                } catch {
                    return false;
                }
            });
        } catch (e) {
            console.error(e);
            console.warn(
                `There was an issue with the included alert routes: ${includedRoutes}.`
            );
            return false;
        }
    };

    const matchExcluded = excludedRoutes => {
        // If no excluded routes, there are no exclusions here
        if (!excludedRoutes?.length) {
            return false;
        }

        // Wrap in try/catch to avoid any errors with misformatted routes
        // If in doubt, do not render
        try {
            const normalized = normalizeRoutes(excludedRoutes);
            // Exclude if ANY matches
            return normalized.some(route => {
                try {
                    const regexp = pathToRegexp(route)?.regexp;
                    return regexp?.test(pathname);
                } catch {
                    return true; // fail-safe: treat invalid excluded route as a match
                }
            });
        } catch (e) {
            console.error(e);
            console.warn(
                `There was an issue with the excluded alert routes: ${excludedRoutes}.`
            );
            return true;
        }
    };

    const matchAlerts = () => {
        return alerts
            ?.filter(alert => alert !== null)
            ?.find(
                ({
                    isGlobal,
                    includedRoutes = { routes: [] },
                    excludedRoutes = { routes: [] },
                    regionCategory,
                    regions,
                }) => {
                    return (
                        matchIncluded(isGlobal, includedRoutes?.routes) &&
                        !matchExcluded(excludedRoutes?.routes) &&
                        matchRegion(regionCategory, regions)
                    );
                }
            );
    };

    const matchRegion = (regionCategory, regions) => {
        const viewerCountry = geoLocation?.viewerCountry || 'Unknown';
        const viewerCountryRegion =
            geoLocation?.viewerCountryRegion || 'Unknown';

        // Map against nested regions object
        // Contentful goes crazy if this field is not an object
        const states = regions?.regions || [];

        // Check for required region category before proceeding
        if (!regionCategory) {
            console.warn(`Region category required for alert render.`);
            return false;
        }

        if (regionCategory === 'Global') {
            console.info('Global match', viewerCountry, viewerCountryRegion);
            return true;
        }

        if (regionCategory === 'Outside US' && viewerCountry !== 'US') {
            console.info(`Outside US`, viewerCountry, viewerCountryRegion);
            return true;
        }

        if (
            (regionCategory === 'US' ||
                regionCategory === 'Core' ||
                regionCategory === 'Growth' ||
                regionCategory === 'Frontier') &&
            viewerCountry === 'US'
        ) {
            const match = states.includes(viewerCountryRegion);

            if (match) {
                return match;
            }
        }

        // Nothing matches
        return false;
    };

    const handleAlerts = () => {
        let matchedAlert = matchAlerts() || null;

        if (matchedAlert !== alert) {
            updateAlert(matchedAlert);
        }
    };

    useEffect(() => {
        const match = document?.cookie?.match(
            /(?:(?:^|.*;\s*)OptanonAlertBoxClosed\s*=\s*([^;]*).*$)|^.*$/,
            '$1'
        );

        if (match?.[1]) {
            setOneTrustInteracted(true);
        }

        if (!window.OptanonWrapper) {
            window.OptanonWrapper = () => {
                window.OneTrust.OnConsentChanged(() => {
                    setOneTrustInteracted(true);
                });
            };
        }
    }, []);

    useEffect(() => {
        if (geoLocation !== null && hasOneTrustInteracted) {
            handleAlerts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geoLocation, pathname, hasOneTrustInteracted]);

    let RenderedAlert = alertMap[alert?.__typename] || null;

    const storeUserViewed = useCallback(() => {
        saveToLocalStorage(uid, ALERT_STATE.CLOSED);
    }, [uid]);

    const storeUserMinimized = useCallback(
        isMinimized => {
            saveToLocalStorage(uid, isMinimized ? ALERT_STATE.MINIMIZED : 0);
        },
        [uid]
    );

    if (
        localStorageSupport() &&
        parseInt(localStorage.getItem(uid), 10) === ALERT_STATE.CLOSED
    ) {
        return null;
    }

    if (RenderedAlert) {
        const isUserStoreMinimized =
            localStorageSupport() &&
            parseInt(localStorage.getItem(uid), 10) === ALERT_STATE.MINIMIZED;

        return (
            // Portal or Tunnel will be rendered based on component
            <RenderedAlert
                {...alert}
                uid={uid}
                isUserStoreMinimized={isUserStoreMinimized}
                storeUserMinimized={storeUserMinimized}
                storeUserViewed={storeUserViewed}
            />
        );
    }

    return null;
};

Alerts.propTypes = {
    attributes: PropTypes.object,
    alerts: PropTypes.array,
};

export default Alerts;
